/**
 * Popup de cobro.
 *
 * El caso que más se usa —efectivo— es el que más ayuda recibe: botones con
 * los billetes reales que circulan en Colombia, un "Exacto" para el caso
 * frecuente, y el cambio calculado en vivo mientras se digita. Si lo recibido
 * no alcanza, el bloque se pone ámbar y el botón de cobrar queda bloqueado:
 * es imposible cerrar una cuenta por menos de lo que vale.
 *
 * La referencia de la transferencia y los últimos 4 de la tarjeta son
 * opcionales a propósito. Sirven para cuadrar la caja al cierre, pero exigirlos
 * frenaría la fila en hora pico.
 */

import { useMemo, useState } from 'react';
import { cop } from '../lib/formato';
import type { DatosCobro } from '../datos/repositorio';
import type { MetodoPago, MetodoUI } from '../tipos/contrato';

const BILLETES = [10_000, 20_000, 50_000, 100_000];

interface Props {
  total: number;
  onCerrar: () => void;
  onConfirmar: (datos: DatosCobro) => void;
}

export function ModalPago({ total, onCerrar, onConfirmar }: Props) {
  const [metodo, setMetodo] = useState<MetodoUI | null>(null);
  const [billetera, setBilletera] = useState<'Nequi' | 'Daviplata'>('Nequi');
  const [tarjeta, setTarjeta] = useState<'Débito' | 'Crédito'>('Débito');
  const [referencia, setReferencia] = useState('');
  const [efectivo, setEfectivo] = useState('');

  const recibido = Number(efectivo) || 0;
  const cambio = recibido - total;

  const listo = useMemo(() => {
    if (metodo === null) return false;
    if (metodo === 'efectivo') return recibido >= total;
    return true;
  }, [metodo, recibido, total]);

  const elegir = (m: MetodoUI) => {
    setMetodo(m);
    setEfectivo('');
    setReferencia('');
  };

  const teclear = (tecla: string) => {
    if (tecla === 'C') return setEfectivo('');
    if (tecla === 'B') return setEfectivo((v) => v.slice(0, -1));
    setEfectivo((v) => (v + tecla).replace(/^0+(?=\d)/, '').slice(0, 9));
  };

  /**
   * Traducción de los tres botones que ve el cajero a los siete métodos que
   * acepta la base. El backend distingue débito de crédito y Nequi de
   * Daviplata porque el arqueo de caja los agrupa distinto; el cajero no
   * debería tener que pensar en eso, solo tocar dos veces.
   */
  const confirmar = () => {
    if (!metodo || !listo) return;

    const traduccion: Record<string, { metodo: MetodoPago; detalle: string }> = {
      'billetera:Nequi': { metodo: 'nequi', detalle: 'Nequi' },
      'billetera:Daviplata': { metodo: 'daviplata', detalle: 'Daviplata' },
      'tarjeta:Débito': { metodo: 'tarjeta_debito', detalle: 'Tarjeta débito' },
      'tarjeta:Crédito': { metodo: 'tarjeta_credito', detalle: 'Tarjeta crédito' },
      'efectivo:': { metodo: 'efectivo', detalle: 'Efectivo' },
    };

    const clave =
      metodo === 'billetera' ? `billetera:${billetera}`
      : metodo === 'tarjeta' ? `tarjeta:${tarjeta}`
      : 'efectivo:';

    const elegido = traduccion[clave];
    if (!elegido) return;

    onConfirmar({
      metodo: elegido.metodo,
      detalle: elegido.detalle,
      // La referencia de Nequi/Daviplata y los últimos 4 de la tarjeta son
      // campos distintos en la base: uno es comprobante, el otro es dato de
      // la franquicia.
      referencia: metodo === 'billetera' ? referencia.trim() || null : null,
      ultimosDigitos: metodo === 'tarjeta' ? referencia.trim() || null : null,
      recibido: metodo === 'efectivo' ? recibido : total,
    });
  };

  return (
    <div className="overlay on" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <div
        className="modal sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tituloPago"
        style={{ maxWidth: 470 }}
      >
        <header>
          <h2 id="tituloPago">Cobrar mesa</h2>
          <button className="close" onClick={onCerrar} aria-label="Cerrar">
            ✕
          </button>
        </header>

        <div className="body">
          <div className="amountbig">
            <div className="eyebrow">Total a pagar</div>
            <div className="v mono">{cop(total)}</div>
          </div>

          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Método de pago
          </div>

          <div className="methods">
            <button
              className="method"
              aria-pressed={metodo === 'billetera'}
              onClick={() => elegir('billetera')}
            >
              <span className="me">📱</span>
              <span className="mt">Nequi / Daviplata</span>
              <span className="ms">Transferencia</span>
            </button>
            <button
              className="method"
              aria-pressed={metodo === 'tarjeta'}
              onClick={() => elegir('tarjeta')}
            >
              <span className="me">💳</span>
              <span className="mt">Tarjeta</span>
              <span className="ms">Débito o crédito</span>
            </button>
            <button
              className="method"
              aria-pressed={metodo === 'efectivo'}
              onClick={() => elegir('efectivo')}
            >
              <span className="me">💵</span>
              <span className="mt">Efectivo</span>
              <span className="ms">Calcula el cambio</span>
            </button>
          </div>

          {metodo === 'billetera' && (
            <div className="paybox">
              <div className="field">
                <label>Aplicación</label>
                <div className="seg">
                  {(['Nequi', 'Daviplata'] as const).map((w) => (
                    <button key={w} aria-pressed={billetera === w} onClick={() => setBilletera(w)}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="ref">Referencia de la transferencia (opcional)</label>
                <input
                  id="ref"
                  className="mono"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  placeholder="Ej. M8374211"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {metodo === 'tarjeta' && (
            <div className="paybox">
              <div className="field">
                <label>Tipo</label>
                <div className="seg">
                  {(['Débito', 'Crédito'] as const).map((t) => (
                    <button key={t} aria-pressed={tarjeta === t} onClick={() => setTarjeta(t)}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="ref">Últimos 4 dígitos (opcional)</label>
                <input
                  id="ref"
                  className="mono"
                  maxLength={4}
                  inputMode="numeric"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value.replace(/\D/g, ''))}
                  placeholder="0000"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {metodo === 'efectivo' && (
            <div className="paybox">
              <div className="received">
                <div>
                  <div className="eyebrow">Recibe del cliente</div>
                  <div className="v mono">{cop(recibido)}</div>
                </div>
                <button className="btn btn-ghost" onClick={() => teclear('C')}>
                  Borrar
                </button>
              </div>

              <div className="quick">
                {BILLETES.map((b) => (
                  <button
                    key={b}
                    className="mono"
                    onClick={() => setEfectivo(String((Number(efectivo) || 0) + b))}
                  >
                    {cop(b)}
                  </button>
                ))}
                <button className="mono" onClick={() => setEfectivo(String(total))}>
                  Exacto
                </button>
                <button className="mono" onClick={() => teclear('000')}>
                  000
                </button>
              </div>

              <div className="pad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} className="mono" onClick={() => teclear(String(n))}>
                    {n}
                  </button>
                ))}
                <button className="mono" onClick={() => teclear('0')}>
                  0
                </button>
                <button className="mono" onClick={() => teclear('00')}>
                  00
                </button>
                <button onClick={() => teclear('B')} aria-label="Borrar último">
                  ⌫
                </button>
              </div>

              <div className={`change${cambio < 0 ? ' short' : ''}`}>
                <span className="l">{cambio < 0 ? 'Faltan' : 'Cambio'}</span>
                <span className="v mono">{cop(Math.abs(cambio))}</span>
              </div>
            </div>
          )}
        </div>

        <footer>
          <button className="btn btn-ghost" onClick={onCerrar}>
            Cancelar
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" disabled={!listo} onClick={confirmar}>
            Cobrar y generar recibo
          </button>
        </footer>
      </div>
    </div>
  );
}
